import { request } from '../utils/request'
import { NextPageContext } from 'next'
import { apiURL } from '../utils/url'
import { apiRoutes } from '../routes'
import moment from 'moment'
import { SpecialSpot } from './specialSpot'

export interface Reserve {
  readonly id: string
  readonly status: number
  readonly userId: string
  readonly name: string
  readonly phoneNumber: string
  readonly menuName: string
  readonly message: string
}

export interface ReservedSpot {
  readonly id: string
  readonly stylistId: string
  readonly salonId: string
  readonly dateTime: string
  readonly spots: number
  readonly releaseTime: string
  readonly reserves: Reserve[]
  readonly deleteFlag: boolean
  readonly specialSpot: SpecialSpot
}

export type ReservedSpots = ReservedSpot[]

export interface Reservation {
  readonly id: number
  readonly userId: number
  readonly status: string
  readonly stylistId: number
  readonly stylistName: string
  readonly stylistImage: string
  readonly salonId: number
  readonly salonName: string
  readonly reserveTime: moment.Moment
  readonly menuId: string
  readonly menuName: string
  readonly menuFee: number
  readonly message: string
  readonly userName: string
  readonly userPhoneNumber: string
  readonly userEmail: string
  readonly createdAt: moment.Moment
}

export async function createReservation(spotId: string, stripeTokenId: string, menuId: string, message: string) {
  const response = await request(apiURL(apiRoutes.reserve), {
    method: 'POST',
    body: JSON.stringify({
      spot_id: spotId,
      menu_id: menuId,
      token: stripeTokenId,
      message: message,
    }),
  })

  return response.ok
}

export async function deleteReservation(reservationId: string) {
  const response = await request(apiURL(apiRoutes.reserveId, reservationId), {
    method: 'DELETE',
  })

  if (!response.ok) {
    throw new Error('Failed to delete reservation')
  }
}

export async function createReservedSpot(
  stylistId: string,
  salonId: string,
  time: string,
  spots: string,
  releaseTime: string,
  special_spot_id: string,
) {
  const response = await request(apiURL(apiRoutes.stylistReservedSpots), {
    method: 'POST',
    body: JSON.stringify({
      stylist_id: stylistId,
      salon_id: salonId,
      time: time,
      spots: spots,
      release_time: releaseTime,
      special_spot_id: special_spot_id,
    }),
  })

  return [response.ok, response.status]
}

export async function updateReservedSpot(
  spotId: string,
  time: string,
  spots: string,
  releaseTime: string,
  specialSpotId: string,
) {
  const response = await request(apiURL(apiRoutes.stylistReservedSpotsId, spotId), {
    method: 'PATCH',
    body: JSON.stringify({
      time: time,
      spots: spots,
      release_time: releaseTime,
      special_spot_id: specialSpotId,
    }),
  })

  return [response.ok, response.status]
}

export async function deleteReservedSpot(spotId: string) {
  const response = await request(apiURL(apiRoutes.stylistReservedSpotsId, spotId), {
    method: 'DELETE',
  })

  return response.ok
}

export async function fetchReservedSpots(
  stylistId: string,
  salonId: string,
  isAll: boolean,
  req?: NextPageContext['req'],
) {
  const reqURL = apiURL(apiRoutes.stylistReservedSpots)
  reqURL.searchParams.append('stylist_id', stylistId)
  reqURL.searchParams.append('salon_id', salonId)

  if (isAll) {
    reqURL.searchParams.append('all', 'true')
  }

  const response = await request(reqURL, {}, req)

  if (!response.ok) {
    return null
  }

  const json = await response.json()
  return reservedSpotsFromJSONList(json)
}

export function reservedSpotsFromJSONList(jsonList: any): ReservedSpots {
  var result: ReservedSpots = []

  jsonList.reserved_spots.forEach((json: any) => {
    result.push(reservedSpotFromJSON(json))
  })
  return result
}

export function reservedSpotFromJSON(json: any): ReservedSpot {
  return {
    id: json.id,
    stylistId: json.hair_stylist_id,
    salonId: json.salon_id,
    dateTime: moment(json.time).format('YYYY/MM/DD HH:mm'),
    spots: json.spots,
    releaseTime: json.release_time,
    reserves: json.reserves ? reserveListFromJSON(json) : new Array(0),
    deleteFlag: json.delete_flag,
    specialSpot: json.special_spot,
  }
}

function reserveListFromJSON(jsonList: any) {
  var result: Reserve[] = []
  console.log(jsonList)

  jsonList.reserves.forEach((json: any) => {
    result.push({
      id: json.id,
      status: json.status,
      userId: json.user_id,
      name: json.name,
      phoneNumber: json.phone_number,
      menuName: json.menu_name,
      message: json.message,
    })
  })
  return result
}

export async function fetchReservations(year: string, req?: NextPageContext['req']): Promise<Reservation[]> {
  const reqURL = apiURL(apiRoutes.reserve)
  reqURL.searchParams.append('year', year)

  const response = await request(reqURL, {}, req)
  const json = await response.json()

  return reservationsFromJSON(json)
}

function reservationsFromJSON(json: any): Reservation[] {
  return json.reservations.map((item: any) => ({
    id: item.id,
    userId: item.user_id,
    status: item.status,
    stylistId: item.stylist_id,
    stylistName: item.stylist_name,
    stylistImage: item.stylist_image,
    salonId: item.salon_id,
    salonName: item.salon_name,
    reserveTime: moment(item.reserve_time),
    menuId: item.menu_id,
    menuName: item.menu_name,
    menuFee: item.menu_fee,
    createdAt: moment(item.created_at),
  }))
}
